"use client"

import { Link } from "react-router-dom"
import { EyeOffIcon } from "lucide-react"

import { HomeTile, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"

import { Button, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"

/** A tile showing the previous period because the board's has no money yet. */
export type TileFallback = { label: string }

/** The bars of a fallback tile are drawn lighter, so the older figures never read as this period's. */
export const FALLBACK_MUTED_CLASS = "opacity-60"

/** The income (teal) and expense (rose) swatches of every F3 tile, one family with the D4 home. */
export const INCOME_BAR_CLASS = "bg-teal-600 dark:bg-teal-400"
export const EXPENSE_BAR_CLASS = "bg-rose-600 dark:bg-rose-400"
export const INCOME_TEXT_CLASS = "text-teal-700 dark:text-teal-300"
export const EXPENSE_TEXT_CLASS = "text-rose-700 dark:text-rose-300"

export function IncomeExpenseLegend({ income, expense }: { income: string; expense: string }) {
  return (
    <span className='flex gap-3 text-xs text-muted-foreground'>
      <span className='flex items-center gap-1.5'>
        <span aria-hidden className={cn("size-2.5 rounded-[3px]", INCOME_BAR_CLASS)} />
        {income}
      </span>
      <span className='flex items-center gap-1.5'>
        <span aria-hidden className={cn("size-2.5 rounded-[3px]", EXPENSE_BAR_CLASS)} />
        {expense}
      </span>
    </span>
  )
}

/** A horizontal bar on a muted track. `ratio` is 0 to 1; a non-zero value is never drawn thinner than a sliver. */
export function TrackBar({ ratio, className, height = "h-2" }: { ratio: number; className: string; height?: string }) {
  return (
    <span className={cn("block overflow-hidden rounded-[4px] bg-muted", height)}>
      <span
        className={cn("block h-full rounded-[4px]", className)}
        style={{ width: `${ratio > 0 ? Math.max(2, ratio * 100) : 0}%` }}
      />
    </span>
  )
}

/**
 * Shown when the server says the figures are not shared with this reader (ADR-0024). Normally the tile is not on
 * such a reader's board at all; this covers a disclosure that changed while the board was open.
 */
export function ChurchTileWithheld() {
  const t = useTranslations("finance.overview.church.common")
  return (
    <div
      data-testid='church-tile-withheld'
      className='flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-border/60 bg-muted/30 px-4 py-5 text-center'
    >
      <span className='inline-flex size-8 items-center justify-center rounded-full bg-background text-muted-foreground ring-1 ring-border/60'>
        <EyeOffIcon aria-hidden className='size-4' />
      </span>
      <p className='text-sm font-semibold text-foreground'>{t("withheld.title")}</p>
      <p className='max-w-sm text-xs text-muted-foreground'>{t("withheld.description")}</p>
    </div>
  )
}

/** A read that failed: say so plainly and offer one retry. */
export function ChurchTileError({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations("finance.overview.church.common")
  return (
    <div data-testid='church-tile-error' className='flex flex-1 flex-col items-start justify-center gap-2'>
      <p className='text-[13px] text-muted-foreground'>{t("error")}</p>
      <Button type='button' variant='outline' size='sm' onClick={onRetry}>
        {t("retry")}
      </Button>
    </div>
  )
}

/**
 * A tile with nothing to show in this period NOR the previous one: one line and, when the reader may record, the
 * way to start. It sizes to its content instead of holding a large empty card open.
 */
export function ChurchTileCompactEmpty({
  title,
  message,
  action,
  testId,
}: {
  title: string
  message: string
  action: HomeTileLink | null
  testId?: string
}) {
  return (
    <HomeTile title={title} testId={testId}>
      <p data-testid='church-tile-compact-empty' className='text-[13px] text-muted-foreground'>
        {message}
        {action ? (
          <>
            {" "}
            <Link to={action.href} className='font-bold text-primary hover:underline'>
              {action.label}
            </Link>
          </>
        ) : null}
      </p>
    </HomeTile>
  )
}
